import { SessionControls } from "../authorization/session_controls";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useBlocker } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { CoreButton } from "../../core/ui/button/button";
import { CoreText, CoreTextType } from "../../core/ui/text/text";
import { MarkdownPreview } from "../../core/ui/markdown/markdown_preview";
import { TaskConfirmDialog } from "../task/task_confirm_dialog";
import { taskLabels } from "../task/task_editor_store";
import { ImportStore } from "./import_store";
import { ImportEntry } from "./import_repository";
import { downloadTemplate, importTemplates } from "./import_templates";
import "../task/task.css";
import "./import.css";

const api = process.env.REACT_APP_API_URL || "http://localhost:4001/api";
const results = {
  pending: "Ожидает",
  created: "Создан",
  duplicate: "Уже в банке",
  error: "Ошибка сохранения",
};

export const ImportScreen = observer(() => {
  const { batch } = useParams();
  const navigate = useNavigate();
  const [store] = useState(() => new ImportStore());
  const [files, setFiles] = useState<File[]>([]);
  const [preview, setPreview] = useState<ImportEntry>();
  const [confirm, setConfirm] = useState(false);
  const blocker = useBlocker(store.busy || store.saving);
  useEffect(() => {
    if (batch) store.load(batch);
    else store.loadHistory();
  }, [store, batch]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (store.busy || store.saving) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [store]);
  const data = store.data;
  return (
    <main className="task-page import-page">
      <SessionControls />
      <Link to="/task">← Банк заданий</Link>
      <header className="task-header">
        <CoreText as="h1" type={CoreTextType.big} text="Импорт заданий" />
        {batch && <CoreButton to="/task/import">Другой пакет</CoreButton>}
      </header>
      {store.error && (
        <div className="error-banner" role="alert">
          <p>{store.error}</p>
          <CoreButton
            disabled={store.busy || store.saving}
            onClick={() =>
              batch
                ? store.load(batch, store.data?.page || 0)
                : store.loadHistory()
            }
          >
            Повторить загрузку
          </CoreButton>
        </div>
      )}
      {store.notice && (
        <p className="success-banner" role="status">
          {store.notice}
        </p>
      )}
      {!batch && (
        <>
          <section className="panel">
            <h2>Markdown или ZIP с изображениями</h2>
            <p className="muted">
              До 1000 заданий и 100 МБ в пакете. Распакованный размер до 300 МБ;
              изображение до 10 МБ. Записи создаются в черновиках.
            </p>
            <label htmlFor="import-files">
              Выберите пакет или несколько Markdown-файлов
            </label>
            <input
              id="import-files"
              type="file"
              accept=".md,.zip"
              multiple
              disabled={store.busy}
              onChange={(event) =>
                setFiles(Array.from(event.target.files || []))
              }
            />
            <p>
              {files.length
                ? `Выбрано файлов: ${files.length}`
                : "Файлы ещё не выбраны"}
            </p>
            <CoreButton
              filled
              disabled={store.busy || !files.length}
              onClick={async () => {
                const id = await store.preview(files);
                if (id) navigate(`/task/import/${id}`);
              }}
            >
              {store.busy
                ? "Разбираем пакет и проверяем изображения…"
                : "Проверить пакет"}
            </CoreButton>
            <h3>Шаблоны четырёх типов</h3>
            <p>
              <a
                href={`${process.env.PUBLIC_URL || ""}/import-example.zip`}
                download
              >
                Скачать пример ZIP-пакета
              </a>
            </p>
            <div className="row-actions">
              {importTemplates.map((item, index) => (
                <CoreButton
                  key={item.name}
                  onClick={() => downloadTemplate(index)}
                >
                  {item.name}
                </CoreButton>
              ))}
            </div>
            <p className="muted">
              Один файл — одно задание. В ZIP можно использовать относительные
              ссылки на изображения. Поддерживается также формат interes.space с
              YAML и разделами «Вопрос» / «Ответ».
            </p>
          </section>
          <section className="panel">
            <h2>Последние пакеты</h2>
            {store.history.length ? (
              store.history.map((item) => (
                <p key={item._id}>
                  <Link to={`/task/import/${item._id}`}>{item.name}</Link> ·{" "}
                  {item.count} заданий ·{" "}
                  {new Date(item.createdAt).toLocaleString("ru-RU")}
                </p>
              ))
            ) : (
              <p className="muted">Пакетов пока нет.</p>
            )}
          </section>
        </>
      )}
      {batch && data && (
        <>
          <section className="panel import-summary">
            <div>
              <h2>{data.batch.name}</h2>
              <p>
                Найдено: {data.batch.count}. Выбрано для сохранения:{" "}
                {data.readyIds.length}.
              </p>
              {data.summary && (
                <p>
                  Создано черновиков: {data.summary.created}. Уже в банке:{" "}
                  {data.summary.duplicates}. Исключено: {data.summary.excluded}.
                </p>
              )}
              <p className="muted">
                Ошибочные записи исправьте в редакторе или оставьте
                исключёнными. Повторное сохранение не создаёт дубликаты.
              </p>
            </div>
            <div className="row-actions">
              <a href={`${api}/task-import/${batch}/report`}>Скачать отчёт</a>
              <CoreButton
                filled
                disabled={store.busy || store.saving || !data.readyIds.length}
                onClick={() => setConfirm(true)}
              >
                Сохранить выбранные
              </CoreButton>
            </div>
          </section>
          {store.saving && (
            <section className="panel" role="status">
              <p>
                Обработано {store.progress.done} из {store.progress.total}
              </p>
              <progress
                max={store.progress.total}
                value={store.progress.done}
                aria-label="Сохранение заданий"
              />
              <CoreButton
                disabled={store.cancelRequested}
                onClick={() => store.cancel()}
              >
                Остановить после порции
              </CoreButton>
            </section>
          )}
          {store.busy && <p role="status">Загрузка…</p>}
          <section className="panel table-scroll">
            <table className="import-table">
              <thead>
                <tr>
                  <th>Выбрать</th>
                  <th>Задание</th>
                  <th>Проверка</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((entry) => (
                  <tr key={entry._id}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Выбрать ${entry.filename}`}
                        checked={entry.selected}
                        disabled={
                          store.busy ||
                          store.saving ||
                          !!entry.issues.length ||
                          ["created", "duplicate"].includes(entry.result)
                        }
                        onChange={(event) =>
                          store.select(entry, event.target.checked)
                        }
                      />
                    </td>
                    <td>
                      <strong>{entry.model.task.title}</strong>
                      <p className="muted import-filename">{entry.filename}</p>
                      <p>
                        {taskLabels[entry.model.task.type]} · вес{" "}
                        {entry.model.scoreAmount}
                      </p>
                    </td>
                    <td>
                      <p>
                        {entry.issues.length
                          ? "Нужно исправить"
                          : results[entry.result]}
                      </p>
                      {entry.issues.map((error, index) => (
                        <p className="import-error" key={index}>
                          {error}
                        </p>
                      ))}
                      {entry.resultError && (
                        <p className="import-error">{entry.resultError}</p>
                      )}
                      {entry.warnings.map((warning, index) => (
                        <p className="muted" key={index}>
                          {warning}
                        </p>
                      ))}
                    </td>
                    <td>
                      <div className="import-actions">
                        <CoreButton onClick={() => setPreview(entry)}>
                          Предпросмотр
                        </CoreButton>
                        {entry.taskId ? (
                          <Link to={`/task/${entry.taskId}/edit`}>
                            Открыть в банке
                          </Link>
                        ) : (
                          <Link to={`/task/import/${batch}/${entry._id}/edit`}>
                            Исправить
                          </Link>
                        )}
                        <a
                          href={`${api}/task-import/${batch}/entries/${entry._id}/original`}
                        >
                          Исходный MD
                        </a>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <footer className="pagination">
              <CoreButton
                disabled={store.busy || store.saving || data.page === 0}
                onClick={() => store.load(batch, data.page - 1)}
              >
                Назад
              </CoreButton>
              <p>
                Страница {data.page + 1} из {Math.ceil(data.batch.count / 25)}
              </p>
              <CoreButton
                disabled={
                  store.busy ||
                  store.saving ||
                  (data.page + 1) * 25 >= data.batch.count
                }
                onClick={() => store.load(batch, data.page + 1)}
              >
                Далее
              </CoreButton>
            </footer>
          </section>
        </>
      )}
      {confirm && data && (
        <TaskConfirmDialog
          titleId="import-confirm"
          onCancel={() => setConfirm(false)}
        >
          <h2 id="import-confirm">
            Создать {data.readyIds.length} черновиков?
          </h2>
          <p>
            Сохранение идёт порциями по 25 заданий. Публикация выполняется
            отдельно в банке.
          </p>
          <div className="row-actions">
            <CoreButton onClick={() => setConfirm(false)}>Вернуться</CoreButton>
            <CoreButton
              filled
              onClick={() => {
                setConfirm(false);
                store.commit();
              }}
            >
              Создать черновики
            </CoreButton>
          </div>
        </TaskConfirmDialog>
      )}
      {preview && (
        <TaskConfirmDialog
          titleId="import-preview"
          style={{ width: 900 }}
          onCancel={() => setPreview(undefined)}
        >
          <CoreButton onClick={() => setPreview(undefined)}>Закрыть</CoreButton>
          <h2 id="import-preview">{preview.model.task.title}</h2>
          <MarkdownPreview
            value={preview.model.task.description || ""}
            label="Условие"
          />
          <h3>Ответы</h3>
          {preview.model.task.answers.map((answer, index) => (
            <p key={index}>
              {typeof answer === "object"
                ? `${answer.isRight ? "✓ " : ""}${answer.text}`
                : String(answer)}
            </p>
          ))}
          <h3>Разбор</h3>
          <MarkdownPreview
            value={preview.model.task.solution || ""}
            label="Разбор"
          />
        </TaskConfirmDialog>
      )}
      {blocker.state === "blocked" && (
        <TaskConfirmDialog
          titleId="import-leave"
          onCancel={() => blocker.reset()}
        >
          <h2 id="import-leave">Дождитесь окончания операции</h2>
          <p>
            Можно остановить сохранение после текущей порции. Уже созданные
            черновики останутся в банке.
          </p>
          <CoreButton onClick={() => blocker.reset()}>Остаться</CoreButton>
        </TaskConfirmDialog>
      )}
    </main>
  );
});
